<div class="loan-table-container bg-white rounded shadow-sm">
  <!-- Header with controls -->
  <div class="table-header d-flex justify-content-between align-items-start p-3 border-bottom bg-light">
    <div class="header-left">
      <h3 class="mb-3 text-dark fw-medium">Loan Numbers</h3>
      <div class="stats-chips">
        <mat-chip-set>
          <mat-chip [highlighted]="true">
            <mat-icon matChipAvatar>list</mat-icon>
            Total: {{ totalCount() }}
          </mat-chip>
          <mat-chip [highlighted]="uniqueCount() !== totalCount()">
            <mat-icon matChipAvatar>verified</mat-icon>
            Unique: {{ uniqueCount() }}
          </mat-chip>
          
          @if (duplicateCount() > 0) {
            <mat-chip color="warn" [highlighted]="true">
                <mat-icon matChipAvatar>warning</mat-icon>
                Duplicates: {{ duplicateCount() }}
            </mat-chip>
          }
          @if (selection.selected.length > 0) {
            <mat-chip color="accent" [highlighted]="true">
                <mat-icon matChipAvatar>check_circle</mat-icon>
                Selected: {{ selectionCount() }}
            </mat-chip>
          }
          @if (isOverMaxLoans()) {
            <mat-chip color="warn" [highlighted]="true">
                <mat-icon matChipAvatar>error</mat-icon>
                Over Limit: +{{ totalCount() - maxLoans() }}
            </mat-chip>
          }
        </mat-chip-set>
      </div>
    </div>
    <div class="header-right d-flex gap-2 align-items-center">
      <div class="duplicate-control d-flex align-items-center gap-2">
        <mat-checkbox 
          [checked]="removeDuplicatesOnPaste()"
          (change)="toggleRemoveDuplicatesOnPaste($event.checked)"
          matTooltip="Automatically remove duplicates when pasting or manually adding loans">
          <span class="small">Remove duplicates</span>
        </mat-checkbox>
      </div>
      <mat-divider [vertical]="true" class="mx-2 header-divider"></mat-divider>
      @if(hasSelection() && selection.selected.length > 1){
        <button matButton="elevated"
            color="primary"
            (click)="removeSelected()"
            [disabled]="!hasSelection()"
            matTooltip="Remove selected loans">
            <mat-icon>delete</mat-icon>
            Remove Selected
        </button>
      }
      <button matButton="elevated"
        color="primary"
        (click)="addNewLoan()"
        [disabled]="hasEditingItem()"
        matTooltip="Add new loan number">
        <mat-icon>add</mat-icon>
        Add Loan
      </button>
      
      <button matButton
        [matMenuTriggerFor]="actionsMenu"
        [disabled]="totalCount() === 0"
        matTooltip="More actions">
        <mat-icon>more_vert</mat-icon>
      </button>
      <mat-menu #actionsMenu="matMenu">
        <button mat-menu-item
          (click)="clearAll()"
          [disabled]="totalCount() === 0">
          <mat-icon>clear_all</mat-icon>
          <span>Clear All</span>
        </button>
        <button mat-menu-item
          (click)="removeSelected()"
          [disabled]="!hasSelection()">
          <mat-icon>delete</mat-icon>
          <span>Remove Selected</span>
        </button>
        <button mat-menu-item
          (click)="removeDuplicates()"
          [disabled]="duplicateCount() === 0">
          <mat-icon>content_copy</mat-icon>
          <span>Remove Duplicates</span>
        </button>
        <mat-divider></mat-divider>
        <button mat-menu-item (click)="selectAll()">
          <mat-icon>select_all</mat-icon>
          <span>Select All</span>
        </button>
        <button mat-menu-item (click)="clearSelection()">
          <mat-icon>deselect</mat-icon>
          <span>Clear Selection</span>
        </button>
      </mat-menu>
    </div>
  </div>

  <!-- Paste Area (shown when empty) -->
  @if (totalCount() === 0) {
    <div #pasteArea
        class="paste-area border-2 border-dashed border-secondary rounded m-3 text-center"
        (paste)="onPaste($event)"
        tabindex="0">
        <div class="paste-placeholder d-flex flex-column align-items-center justify-content-center h-100 text-muted">
            <mat-icon class="mb-3">
                content_paste
            </mat-icon>
            <p class="mb-1">Click here and paste loan numbers (Ctrl+V)</p>
            <p class="small opacity-75">
                Supports comma, space, tab, pipe, or newline separated values
            </p>
        </div>
    </div>
  }

  <!-- Table -->
  <div class="table-wrapper flex-grow-1 overflow-auto"
    (paste)="onPaste($event)"
    tabindex="0">
    <table mat-table matSort
        [dataSource]="dataSource"
        #tableSort="matSort"
        class="loan-table w-100">

        <!-- Checkbox Column -->
        <ng-container matColumnDef="select">
            <th mat-header-cell *matHeaderCellDef class="checkbox-column">
                <mat-checkbox
                    (change)="$event ? toggleAllRows() : null"
                    [checked]="selection.hasValue() && isAllSelected()"
                    [indeterminate]="selection.hasValue() && !isAllSelected()"
                    matTooltip="Select all"
                    [ariaLabel]="'Select all rows'">
                </mat-checkbox>
            </th>
            <td mat-cell *matCellDef="let row" class="checkbox-column">
                <mat-checkbox
                    (click)="onCheckboxClick(row, $event)"
                    [checked]="selection.isSelected(row)"
                    matTooltip="Select row (Shift+Click for range)"
                    matTooltipPosition="left"
                    [ariaLabel]="'Select loan ' + row.loan">
                </mat-checkbox>
            </td>
        </ng-container>

      <!-- Loan Number Column -->
      <ng-container matColumnDef="loan">
        <th mat-header-cell *matHeaderCellDef mat-sort-header class="loan-column">
          Loan Number
        </th>
        <td mat-cell *matCellDef="let row" class="loan-column p-3" [class]="getLoanCellClasses(row)">
            @if (row.isEditing) {
                <!-- Edit Mode -->
                <mat-form-field appearance="outline" class="w-100">
                    <input #editInput matInput
                        [formControl]="editFormControl"
                        placeholder="Enter loan number"
                        (keydown.enter)="saveEdit(row)"
                        (keydown.escape)="cancelEdit(row)"
                        [attr.aria-label]="'Edit loan number ' + (row.loan || 'new loan')" />
                    @if (editFormControl.hasError('required')) {
                        <mat-error>Loan number is required</mat-error>
                    }
                </mat-form-field>
            } @else {
                <!-- Display Mode -->
                <div class="loan-display d-flex align-items-center gap-2">
                    <span class="loan-number font-monospace fw-medium"
                        [class.text-warning]="row.isDuplicate">
                    {{ row.loan }}
                    </span>
                    @if (row.isDuplicate) {
                        <mat-icon class="text-warning" matTooltip="Duplicate loan number">
                            warning
                        </mat-icon>
                    }
                </div>
            }
        </td>
      </ng-container>

        <!-- Actions Column -->
        <ng-container matColumnDef="actions">
            <th mat-header-cell *matHeaderCellDef class="actions-column text-end">
                Actions
            </th>
            <td mat-cell *matCellDef="let row" class="actions-column text-end pe-3">
                @if (row.isEditing) {
                    <!-- Edit Actions -->
                    <div class="d-flex">
                        <button matIconButton
                            color="primary"
                            (click)="saveEdit(row)"
                            [disabled]="!editFormControl.valid"
                            matTooltip="Save">
                            <mat-icon>check</mat-icon>
                        </button>
                        <button matIconButton (click)="cancelEdit(row)" matTooltip="Cancel">
                            <mat-icon>close</mat-icon>
                        </button>
                    </div>
                } @else {
                    <!-- Display Actions -->
                    <div class="d-flex">
                        <button matIconButton
                            (click)="startEdit(row)"
                            matTooltip="Edit loan number"
                            [attr.aria-label]="'Edit loan ' + row.loan">
                            <mat-icon>edit</mat-icon>
                        </button>
                        <button matIconButton
                            color="warn"
                            (click)="removeLoan(row.id)"
                            matTooltip="Remove loan"
                            [attr.aria-label]="'Remove loan ' + row.loan">
                            <mat-icon>delete</mat-icon>
                        </button>
                    </div>
                }
            </td>
        </ng-container>

        <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
        <tr mat-row *matRowDef="let row; columns: displayedColumns;" 
            [class]="getRowClasses(row)"
            (click)="onRowClick(row, $event)"
            [style.cursor]="row.isEditing ? 'default' : 'pointer'"
            [attr.title]="row.isEditing ? '' : 'Click to select, Shift+Click for range, Ctrl+Click to toggle'"></tr>
    </table>

    <!-- No data message - shown when table is empty -->
    @if (totalCount() === 0) {
        <div class="no-data d-flex flex-column align-items-center justify-content-center p-5 text-muted text-center">
            <mat-icon class="mb-3">inbox</mat-icon>
            <p>No loan numbers added yet</p>
        </div>
    }
  </div>

  <!-- Paginator -->
  <mat-paginator showFirstLastButtons
    [length]="totalCount()"
    [pageSize]="pageSize()"
    [pageSizeOptions]="pageSizeOptions()"
    [showFirstLastButtons]="true"></mat-paginator>

  <!-- Footer with limits info -->
  <div class="table-footer d-flex justify-content-between align-items-center p-3 border-top"
    [class.bg-warning-subtle]="isOverMaxLoans()"
    [class.bg-light]="!isOverMaxLoans()">
    <div class="limits-info d-flex align-items-center gap-2 text-muted small">
      <span [class.text-warning]="isOverMaxLoans()" [class.fw-semibold]="isOverMaxLoans()">
        <strong>{{ totalCount() }}</strong> of
        <strong>{{ maxLoans() }}</strong> loans
      </span>
      @if (isOverMaxLoans()) {
        <mat-icon class="text-warning" matTooltip="Exceeds maximum loans">
            warning
        </mat-icon>
        <span class="text-warning fw-semibold">
          (+{{ totalCount() - maxLoans() }} over limit)
        </span>
      }
    </div>
    @if (totalCount() > 0) {
      <div class="selection-help d-flex align-items-center text-muted small">
        <span matTooltip="Click row or checkbox to select, Shift+Click for range selection, Ctrl/Cmd+Click to toggle individual items">
          <mat-icon class="small-icon">info</mat-icon>
          Multi-select: Shift+Click (range), Ctrl+Click (toggle)
        </span>
      </div>
    }
  </div>
</div>
